/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ProtectedRoute = ({ children, requiredRole = null, redirectTo = '/login' }) => {
  const { isAuthenticated, loading, hasRole, user } = useAuth();
  const location = useLocation();

  if (loading) {
    // Show loading spinner while checking authentication
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Store the intended destination (pathname + state) for after login
    const dest = { pathname: location.pathname, state: location.state };
    sessionStorage.setItem('intendedDestination', JSON.stringify(dest));
    console.log('🔐 ProtectedRoute - Storing intended destination:', location.pathname);
    
    // Redirect to login if not authenticated
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Profile setup is now optional - users can access dashboard directly
  // The profile setup component will auto-redirect to dashboard
  // No need to check profileCompleted anymore

  if (requiredRole && !hasRole(requiredRole)) {
    // Redirect to appropriate dashboard if user doesn't have required role
    const userRole = user?.role?.toLowerCase();
    
    console.log('🔐 ProtectedRoute - Role mismatch:', {
      userRole,
      requiredRole,
      location: location.pathname
    });
    
    if (userRole === 'recruiter') {
      return <Navigate to="/recruiter-dashboard" replace />;
    } else if (userRole === 'employer') {
      return <Navigate to="/employer-dashboard" replace />;
    } else if (userRole === 'jobseeker' || userRole === 'job_seeker') {
      return <Navigate to="/jobseeker-dashboard" replace />;
    } else if (userRole === 'intern') {
      return <Navigate to="/intern-dashboard" replace />;
    } else if (userRole === 'university' || userRole === 'universityadmin') {
      return <Navigate to="/university-dashboard" replace />;
    } else if (userRole === 'institute') {
      return <Navigate to="/institute-dashboard" replace />;
    } else if (userRole === 'association' || userRole === 'assoc') {
      return <Navigate to="/association-dashboard" replace />;
    } else if (userRole === 'ngo') {
      return <Navigate to="/ngo-dashboard" replace />;
    } else if (userRole === 'admin' || userRole === 'administrator') {
      return <Navigate to="/admin" replace />;
    }
    // Fallback to home page
    console.warn('⚠️ ProtectedRoute - Unknown role, redirecting to home:', userRole);
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute;
